import type { FieldPath } from 'react-hook-form';

import type { DiscountFormValues } from './types';

/** The Create wizard's three steps: 1 Discount Details · 2 Trigger · 3 Quantity Rule. */
export type DiscountWizardStep = 1 | 2 | 3;

export type ResolvedDiscountDetail = {
  /** An RHF path this form actually renders an input for. */
  path: FieldPath<DiscountFormValues>;
  /** The wizard step holding that input, so the admin lands where the error is visible. */
  step: DiscountWizardStep;
};

/**
 * `DISCOUNT_VALIDATION_FAILED` `details[].field` → the RHF path that renders it.
 *
 * Every entry is verified against a real input in the Create wizard: a `setError` on a path no
 * control is bound to is silently invisible, which is the same defect ADO 114870 fixes. Three
 * spellings differ from the wire deliberately — the form's Applicability arms are named for what
 * they hold (`bundles`, `customers`) where the backend names the id arrays it receives — and
 * `trigger.dateRange.from` folds onto the single `<DateRangeField>` that owns both ends of the
 * window.
 *
 * Backend paths **deliberately absent**, because the form binds no error-rendering control to
 * them, so they route to the caller's fallback toast instead:
 *   `applicability.applicableTo`, `trigger.type`, `quantityRule.tierBehavior` — bare `<RadioGroup>`s
 *   with no `Controller`; `quantityRule.tiers` and `quantityRule.tiers[i]` — set-level and
 *   row-level issues (a gap or an overlap) that belong to no single input.
 */
const FIELD_MAP: Record<string, ResolvedDiscountDetail> = {
  name: { path: 'name', step: 1 },
  value: { path: 'value', step: 1 },
  'applicability.devices': { path: 'applicability.devices', step: 1 },
  'applicability.bundleIds': { path: 'applicability.bundles', step: 1 },
  'applicability.customerIds': { path: 'applicability.customers', step: 1 },
  'applicability.applicableTo.devices': { path: 'applicability.applicableTo.devices', step: 1 },
  'applicability.applicableTo.bundleIds': { path: 'applicability.applicableTo.bundles', step: 1 },
  'trigger.dateRange': { path: 'trigger.dateRange', step: 2 },
  'trigger.dateRange.from': { path: 'trigger.dateRange', step: 2 },
};

/**
 * The per-tier paths with an input of their own: the row's Min Qty, Max Qty and its own Discount
 * Amount/Percentage. All three render their error under the input (`<TierField>`), so all three
 * resolve; only set-level and row-level verdicts (a gap, an overlap) belong to no single input.
 *
 * Min/Max arrive from the **payload** layer rather than the domain one — the tier qty cap answers
 * 400 `VALIDATION_FAILED` naming `quantityRule.tiers.0.maxQty` (probed live 2026-09-22, ADO
 * 117110) — which is why the caller must branch on that code too, not the 422 alone.
 */
const TIER_FIELD = /^quantityRule\.tiers\.(\d+)\.(minQty|maxQty|value)$/;

/**
 * Which nested picker the Customer arm currently renders, or `null` outside that arm.
 *
 * The backend's price checks run over the **normalized** `deviceIds`/`bundleIds` whatever the
 * applicability scope, so a customer-scoped payload still comes back naming `applicability.devices`
 * (verified live 2026-09-11: a Customer → Applicable To → Device create returned HTTP 422 with
 * `field: "applicability.devices"`). That picker is not on screen in the Customer arm — only
 * `applicability.applicableTo.devices` is — so mapping the path literally set an error on an
 * invisible control and showed the admin nothing at all (ADO 114870).
 */
export type NestedApplicabilityScope = 'device' | 'bundle' | null;

/** The two top-level paths the Customer arm renders under `applicableTo` instead. */
const NESTED_REDIRECT: Record<string, { scope: 'device' | 'bundle'; field: string }> = {
  'applicability.devices': { scope: 'device', field: 'applicability.applicableTo.devices' },
  'applicability.bundleIds': { scope: 'bundle', field: 'applicability.applicableTo.bundleIds' },
};

/**
 * Resolve one backend detail onto an input this form renders, or `null` when none holds it.
 *
 * `tierCount` is the number of tier rows currently on screen: an index past the end has no input,
 * so it is reported unmapped rather than setting an error nothing can show.
 */
export function resolveDiscountDetailField(
  field: string,
  tierCount: number,
  nestedScope: NestedApplicabilityScope = null,
): ResolvedDiscountDetail | null {
  // The wire uses bracket indexing (`quantityRule.tiers[1].value`); RHF wants dots.
  const normalized = field.replace(/\[(\d+)\]/g, '.$1');
  const tier = TIER_FIELD.exec(normalized);
  if (tier) {
    const index = Number(tier[1]);
    if (index >= tierCount) return null;
    return {
      path: `quantityRule.tiers.${index}.${tier[2] as 'minQty' | 'maxQty' | 'value'}`,
      step: 3,
    };
  }
  // Redirect only when the arm on screen matches the verdict's own kind. A device verdict while
  // the Bundle sub-scope is rendered is left on its top-level path: neither picker can hold it,
  // so it must stay unmapped-in-practice rather than be forced onto the wrong control.
  const redirect = NESTED_REDIRECT[normalized];
  const target = redirect && redirect.scope === nestedScope ? redirect.field : normalized;
  return FIELD_MAP[target] ?? null;
}
